import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import TextField from '@mui/material/TextField';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from '../../../hooks/useTranslation';
import { WorkingHours } from '../../../interfaces/Agent';
import StyledFormItem from '../../../styles/StyledFormItem';
import Text from '../Text/Text';

interface WorkingHoursPickerProps {
  name: string;
}

const WEEKDAYS = [
  { value: 0, labelKey: 'Mon' },
  { value: 1, labelKey: 'Tue' },
  { value: 2, labelKey: 'Wed' },
  { value: 3, labelKey: 'Thu' },
  { value: 4, labelKey: 'Fri' },
  { value: 5, labelKey: 'Sat' },
  { value: 6, labelKey: 'Sun' },
] as const;

const isFullyCleared = (value: Partial<WorkingHours>): boolean =>
  (value.days?.length ?? 0) === 0 && !value.start && !value.end;

const WorkingHoursPicker: React.FC<WorkingHoursPickerProps> = ({ name }) => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const value = field.value as WorkingHours | undefined;

        const commit = (patch: Partial<WorkingHours>) => {
          const merged: WorkingHours = {
            days: patch.days ?? value?.days ?? [],
            start: patch.start ?? value?.start ?? '',
            end: patch.end ?? value?.end ?? '',
          };
          field.onChange(isFullyCleared(merged) ? undefined : merged);
        };

        return (
          <>
            <StyledFormItem>
              <Text type="label">{t('Working Days')}</Text>
              <ToggleButtonGroup
                value={value?.days ?? []}
                onChange={(_, days: number[]) => commit({ days })}
                aria-label={t('Working Days')}
                fullWidth
              >
                {WEEKDAYS.map(day => (
                  <ToggleButton key={day.value} value={day.value}>
                    {t(day.labelKey)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </StyledFormItem>

            <StyledFormItem>
              <Text type="label">{t('Start Time')}</Text>
              <TextField
                type="time"
                value={value?.start ?? ''}
                onChange={event => commit({ start: event.target.value })}
                inputProps={{ 'aria-label': t('Start Time') }}
              />
            </StyledFormItem>

            <StyledFormItem>
              <Text type="label">{t('End Time')}</Text>
              <TextField
                type="time"
                value={value?.end ?? ''}
                onChange={event => commit({ end: event.target.value })}
                inputProps={{ 'aria-label': t('End Time') }}
              />
            </StyledFormItem>
          </>
        );
      }}
    />
  );
};

export default WorkingHoursPicker;
